<template>
  <div style="width: 100%">
    <div class="row">
      <div class="col-sm-12">
        <slot />

        <!-- Search filter -->
        <div class="search-input">
          <input
            type="search"
            class="form-control"
            placeholder="Search..."
            v-model="searchText"
            data-test="su-search-users"
            aria-label="Search for users to assign..."
          />
        </div>

        <!-- List -->
        <div>
          <div class="list-container">
            <h4 class="text-center text-muted" v-if="filteredResults === null && !isLoading">
              Search for users to assign...
            </h4>
            <h4 class="text-center" v-else-if="filteredResults === null && isLoading">
              Searching...
            </h4>
            <div v-if="filteredResults && !isLoading && filteredResults.length === 0">
              <h4 class="text-muted text-center" data-test="su-no-results">No matching results.</h4>
            </div>
            <div
              v-for="result in filteredResults"
              :key="result.id"
              class="user-list-item"
              data-test="su-results"
            >
              <button
                class="btn btn-default"
                style="width: 100%"
                @click="selectUser(result)"
                type="button"
              >
                <span class="user-text">{{ result.firstName }} {{ result.lastName }}</span>
                <span class="user-text email">&lt;{{ result.email }}&gt;</span>
                <span
                  v-if="result.organisation"
                  class="organisation label"
                  :class="result === selectedUser ? 'label-primary' : 'label-default'"
                  >{{ result.organisation.name }}</span
                >
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row" v-if="props.errors && props.errors.length">
      <br />
      <div class="col-sm-12">
        <p
          class="col-sm-12 text-danger text-left"
          v-for="error in props.errors"
          :key="error"
          data-test="su-error"
        >
          {{ error }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from "axios";
import { computed, ref, watch } from "vue";
import { AdminSearchResults } from "../../../views/admin/admin-search-page.vue";
import { ShortUserDetailsApiModel } from "../../../api-models";

interface Props {
  existingMembers: ShortUserDetailsApiModel[];
  errors?: string[];
}

const props = defineProps<Props>();

const emit = defineEmits(["select-user", "update-search-state"]);

const searchText = ref("");

const searchResults = ref<ShortUserDetailsApiModel[] | null>(null);

const selectedUser = ref<ShortUserDetailsApiModel | null>(null);

const serverCallTimeout = ref<any>(null);

const isLoading = ref(false);

const filteredResults = computed<ShortUserDetailsApiModel[] | null>(() => {
  if (!searchResults.value) {
    return null;
  }

  const existingMemberIds = props.existingMembers.map((m) => {
    return m.id;
  });
  return searchResults.value.filter((u) => !existingMemberIds?.includes(u.id));
});

const selectUser = (selection: ShortUserDetailsApiModel): void => {
  emit("select-user", selection);
};

const searchUsers = (): void => {
  if (!searchText.value || searchText.value.trim().length < 3) {
    searchResults.value = null;
    return;
  }
  if (serverCallTimeout.value) {
    clearTimeout(serverCallTimeout.value);
  }

  serverCallTimeout.value = setTimeout(async () => {
    const query = searchText.value.trim();
    searchResults.value = [];
    selectedUser.value = null;
    isLoading.value = true;
    serverCallTimeout.value = null;
    const response = await axios.get(`/admin/search/query?q=${query}`);
    const results = response.data as AdminSearchResults;
    searchResults.value = results?.Users;
    isLoading.value = false;
  }, 500);
};

watch(searchText, (newSearchText) => {
  if (newSearchText) {
    searchUsers();
  }
});

watch(isLoading, () => {
  emit("update-search-state", isLoading.value);
});
</script>

<style scoped lang="less">
@import "../../../styles/bootstrap/variables.less";

.list-container {
  max-height: 300px;
  min-height: 200px;
  overflow-y: scroll;
  margin-top: 20px;
  border: 1px solid lightgray;

  // for firefox
  scrollbar-width: thin;
  scrollbar-color: @form-text-color transparent;
  // for the other browsers
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: @form-text-color;
    border-radius: 20px;
  }

  .user-list-item {
    margin: 10px;
    cursor: pointer;
    clear: both;
    width: 95%;

    button {
      white-space: normal;
      word-wrap: break-word;
    }

    .organisation {
      clear: both;
      display: block;
      float: left;
      margin-left: 2rem;
      margin-bottom: 10px;
    }

    &.selected {
      color: @link-color;
      font-weight: bold;
    }
  }
}

.search-input {
  width: 100%;
  input {
    max-width: 100%;
  }
}

.user-text {
  max-width: 90%;
  overflow-wrap: break-word;
  display: block;
  margin-left: 2rem;
  text-align: left;
}

.email {
  color: @form-text-color;
}
</style>
